import { useState, useEffect } from 'react';
import { integrationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';
import { toast } from 'sonner';
import { Loader2, ExternalLink, Check, PlugZap, Send, Zap, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';

const HOW_TO_CONNECT = {
  hubspot: [
    'Sign in at app.hubspot.com → click your profile (top-right) → Account & Billing.',
    "Go to 'Integrations → Private Apps' and click 'Create a private app'.",
    'Name it "DotCall" and on the Scopes tab grant: crm.objects.contacts.read/write, crm.objects.deals.read/write, communications.read/write.',
    "Click 'Create app' → confirm → copy the Access Token (starts with pat-na1-...).",
    'Paste it into the API Key field below and Save Configuration → Test Connection.',
  ],
  zoho_crm: [
    'Go to api-console.zoho.com (use the same data centre as your CRM — .com / .in / .eu).',
    "Click 'Add Client' → choose 'Self Client' → Create. Copy the Client ID + Client Secret.",
    "Open the 'Generate Code' tab. Set Scope: ZohoCRM.modules.ALL,ZohoCRM.org.READ,ZohoCRM.users.READ — Time Duration: 10 minutes — Description: DotCall. Click Create.",
    'Copy the generated grant code. In a terminal, exchange it for a refresh_token (one-time):',
    "curl -X POST 'https://accounts.zoho.{dc}/oauth/v2/token' -d 'grant_type=authorization_code&client_id=...&client_secret=...&code=GRANT_CODE&redirect_uri=https://www.zoho.com'",
    'From the JSON response, copy the "refresh_token" — it never expires.',
    'Pick the same Data Centre below, paste Client ID, Client Secret and Refresh Token, then Save → Test Connection.',
  ],
  salesforce: [
    'In Salesforce, go to Setup → App Manager → New Connected App (Lightning).',
    "Fill basic info, then under 'API (Enable OAuth Settings)' tick 'Enable OAuth Settings'. Set Callback URL: https://login.salesforce.com/services/oauth2/callback. Add scopes: Manage user data via APIs (api), Perform requests at any time (refresh_token, offline_access).",
    "Tick 'Enable Client Credentials Flow' OFF and 'Require Secret for Web Server Flow' ON. Save (allow up to 10 min for propagation).",
    'In the new app, click Manage → Edit Policies → set "IP Relaxation" to "Relax IP restrictions" (or whitelist server IPs).',
    "Back on the app's view, click 'Manage Consumer Details' → copy Consumer Key (Client ID) and Consumer Secret.",
    'Find your Salesforce username + password. If your org enforces MFA, also reset and copy your security_token (Setup → personal info → Reset My Security Token — Salesforce mails it).',
    'Pick Production / Sandbox below. Paste all 5 fields → Save → Test Connection.',
  ],
  whatsapp_business: [
    'Go to developers.facebook.com → My Apps → Create App → "Business" type.',
    "Add the 'WhatsApp' product. Open WhatsApp → API Setup. Note the test Phone Number ID.",
    'Click "Generate Access Token" — for production, instead create a System User with whatsapp_business_messaging + whatsapp_business_management permissions and generate a never-expiring token.',
    "Add a recipient phone number (yours, for testing) and click 'Send message' from the API Setup screen — verify you receive it.",
    "Paste Phone Number ID + Access Token below. Leave api_version as v20.0. Save → Test Connection.",
    "Use 'Send Test Demo' below to fire a one-off message to any number. Toggle 'Auto-send demo' to fire on caller intent during AI calls.",
  ],
  google_sheets: [
    'In Google Cloud Console, create or pick a project → enable the Google Sheets API.',
    "APIs & Services → Credentials → Create credentials → Service account. Name it 'DotCall'.",
    "On the service account, go to Keys → Add key → Create new key → JSON. Save the downloaded file.",
    'Open your spreadsheet → click Share → paste the service account email (looks like name@project.iam.gserviceaccount.com) → give it Editor access.',
    "Copy the spreadsheet ID from the URL (between /d/ and /edit). Paste it below.",
    'Open the JSON file in a text editor → copy ALL its content → paste into the Service Account JSON field. Save → Test Connection.',
  ],
  zapier: [
    'In Zapier, create a new Zap. Trigger app: "Webhooks by Zapier" → "Catch Hook".',
    "Click Continue — Zapier will give you a webhook URL like https://hooks.zapier.com/hooks/catch/.../...",
    "Copy that URL and paste it below. Save Configuration → Test Connection (we'll fire a sample payload Zapier can pick up).",
    "Back in Zapier, click 'Test trigger' to see the payload, then add any Action you want (Slack, Email, Sheets, etc.).",
  ],
};

const INTEGRATIONS = [
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Sync contacts, deals, and call logs with HubSpot CRM. Push call transcripts and outcomes automatically.',
    color: '#FF7A59',
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'pat-na1-xxxxxxxx', type: 'password' },
    ],
    docs: 'https://developers.hubspot.com/docs/api/overview',
  },
  {
    id: 'zoho_crm',
    name: 'Zoho CRM',
    description: 'Push every completed call as a Lead + Call activity into Zoho CRM. Uses OAuth refresh-token flow — generate a Self Client in Zoho API Console.',
    color: '#E42527',
    fields: [
      {
        key: 'dc',
        label: 'Data Center',
        type: 'select',
        options: [
          { value: 'in', label: 'India (.in)' },
          { value: 'com', label: 'US (.com)' },
          { value: 'eu', label: 'Europe (.eu)' },
          { value: 'au', label: 'Australia (.com.au)' },
          { value: 'jp', label: 'Japan (.jp)' },
        ],
      },
      { key: 'client_id', label: 'Client ID', placeholder: '1000.XXXXXXXXXXXXXXXXX', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'xxxxxxxxxxxxxxxxxxxx', type: 'password' },
      { key: 'refresh_token', label: 'Refresh Token', placeholder: '1000.xxxxx.xxxxx', type: 'password' },
    ],
    docs: 'https://www.zoho.com/crm/developer/docs/api/v5/auth-request.html',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Sync calls as Leads + Tasks in Salesforce. Uses OAuth Username-Password flow — create a Connected App in Salesforce Setup.',
    color: '#00A1E0',
    fields: [
      {
        key: 'login_url',
        label: 'Login URL',
        type: 'select',
        options: [
          { value: 'https://login.salesforce.com', label: 'Production / Developer' },
          { value: 'https://test.salesforce.com', label: 'Sandbox' },
        ],
      },
      { key: 'client_id', label: 'Consumer Key (Client ID)', placeholder: '3MVG9...', type: 'text' },
      { key: 'client_secret', label: 'Consumer Secret', placeholder: 'xxxxxxxxxxxxxxxxxxxx', type: 'password' },
      { key: 'username', label: 'Username', placeholder: 'you@example.com', type: 'text' },
      { key: 'password', label: 'Password', placeholder: 'your password', type: 'password' },
      { key: 'security_token', label: 'Security Token (optional)', placeholder: 'leave blank if IP allow-listed', type: 'password' },
    ],
    docs: 'https://help.salesforce.com/s/articleView?id=sf.connected_app_create.htm',
  },
  {
    id: 'whatsapp_business',
    name: 'WhatsApp Business',
    description: 'Send instant WhatsApp demos when a caller asks for one. Uses the Meta WhatsApp Cloud API. Configure a Phone Number ID + permanent Access Token from Meta for Developers.',
    color: '#25D366',
    fields: [
      { key: 'phone_number_id', label: 'Phone Number ID', placeholder: '1100000000000000', type: 'text' },
      { key: 'access_token', label: 'Permanent Access Token', placeholder: 'EAAGxxxxxxxxxx', type: 'password' },
      { key: 'api_version', label: 'API Version', placeholder: 'v20.0', type: 'text' },
      {
        key: 'default_demo_message',
        label: 'Default Demo Message',
        placeholder: "Hi! Thanks for chatting with our AI agent. Here's the demo you asked for: https://dotcall.ai\n— Team DotCall",
        type: 'textarea',
      },
    ],
    docs: 'https://developers.facebook.com/docs/whatsapp/cloud-api',
    extras: 'whatsapp',
  },
  {
    id: 'google_sheets',
    name: 'Google Sheets',
    description: 'Export call logs, campaign results, and analytics to Google Sheets. Import contacts from spreadsheets.',
    color: '#34A853',
    fields: [
      { key: 'spreadsheet_id', label: 'Spreadsheet ID', placeholder: 'e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms', type: 'text' },
      { key: 'service_account_json', label: 'Service Account JSON Key', placeholder: 'Paste JSON key content', type: 'password' },
    ],
    docs: 'https://developers.google.com/sheets/api/quickstart/python',
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Connect DotCall to 5000+ apps via Zapier. Trigger workflows on call completion, new contacts, and more.',
    color: '#FF4F00',
    fields: [
      { key: 'webhook_url', label: 'Webhook URL', placeholder: 'https://hooks.zapier.com/hooks/catch/xxxxx/xxxxx/', type: 'text' },
    ],
    docs: 'https://zapier.com/developer/documentation/v2/rest-hooks/',
  },
];

function HowToConnectPanel({ integrationId }) {
  const [open, setOpen] = useState(false);
  const steps = HOW_TO_CONNECT[integrationId] || [];
  if (steps.length === 0) return null;
  return (
    <div className="mt-3 rounded-lg border border-white/5 bg-slate-950/60" data-testid={`how-to-${integrationId}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`how-to-toggle-${integrationId}`}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors"
      >
        <span className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-amber-400" /> How to connect
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <ol className="px-4 pb-4 pt-1 space-y-2 text-xs text-slate-300 list-decimal list-inside">
          {steps.map((step, i) => (
            <li key={i} className="leading-relaxed">{step}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

function WhatsappTestPanel({ config, onAfterSend }) {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const fields = config.fields || {};
  const autoSend = !!fields.auto_send_demo;

  const handleSend = async () => {
    if (!phone.trim()) {
      toast.error('Enter a phone number');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/integrations/whatsapp_business/send-test', {
        to_number: phone.trim(),
        message: message.trim() || undefined,
      });
      toast.success(`Sent! message_id: ${res.data?.message_id || 'ok'}`);
      onAfterSend?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'WhatsApp send failed');
    }
    setSending(false);
  };

  const toggleAutoSend = () => {
    onAfterSend?.({ auto_send_demo: !autoSend });
  };

  return (
    <div className="mt-2 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20" data-testid="whatsapp-test-panel">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold">
            <Zap className="w-4 h-4" /> Auto-send demo on caller intent
          </div>
          <p className="text-xs text-slate-400 mt-0.5 max-w-xl">
            When the caller asks for a demo and shares a phone/WhatsApp number on the call,
            DotCall will automatically send the default demo message to that number.
          </p>
        </div>
        <Switch
          checked={autoSend}
          onCheckedChange={toggleAutoSend}
          data-testid="whatsapp-auto-send-toggle"
        />
      </div>

      <div className="border-t border-emerald-500/10 pt-3">
        <Label className="text-slate-300 text-sm mb-2 block flex items-center gap-2">
          <Send className="w-3.5 h-3.5 text-emerald-400" /> Send Test WhatsApp Demo
        </Label>
        <div className="grid md:grid-cols-2 gap-3">
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number e.g., 9876543210 or +919876543210"
            data-testid="whatsapp-test-phone"
            className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600"
          />
          <Button
            onClick={handleSend}
            disabled={sending}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="whatsapp-test-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Test Demo
          </Button>
        </div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Override message (optional — uses default demo message if blank)"
          rows={2}
          data-testid="whatsapp-test-message"
          className="mt-2 bg-slate-950 border-white/10 text-white placeholder:text-slate-600"
        />
        <p className="text-[11px] text-slate-500 mt-2">
          ⚠️ Save Configuration first. Free-form text only works inside the 24-hour
          customer-care window — outside it, Meta requires an approved template.
        </p>
      </div>
    </div>
  );
}

export default function IntegrationsPage() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);

  const reload = async () => {
    try {
      const res = await integrationsAPI.list();
      const data = {};
      (res.data || []).forEach(i => { data[i.id] = i; });
      setConfigs(data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getConfig = (id) => configs[id] || { id, enabled: false, fields: {} };

  const updateField = (integrationId, fieldKey, value) => {
    setConfigs(prev => ({
      ...prev,
      [integrationId]: {
        ...getConfig(integrationId),
        fields: { ...(getConfig(integrationId).fields || {}), [fieldKey]: value }
      }
    }));
  };

  const toggleEnabled = (integrationId) => {
    setConfigs(prev => ({
      ...prev,
      [integrationId]: {
        ...getConfig(integrationId),
        enabled: !getConfig(integrationId).enabled
      }
    }));
  };

  const handleSave = async (integrationId, fieldOverrides = null) => {
    setSaving(integrationId);
    try {
      const config = getConfig(integrationId);
      const fields = fieldOverrides
        ? { ...(config.fields || {}), ...fieldOverrides }
        : (config.fields || {});
      await integrationsAPI.save({ id: integrationId, enabled: config.enabled, fields });
      // Reflect overrides locally if any
      if (fieldOverrides) {
        setConfigs(prev => ({
          ...prev,
          [integrationId]: { ...getConfig(integrationId), fields },
        }));
      }
      toast.success(`${integrationId} configuration saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const handleTest = async (integrationId) => {
    setTesting(integrationId);
    try {
      const res = await integrationsAPI.test(integrationId);
      if (res.data?.success) {
        toast.success(res.data?.message || `${integrationId} connection successful!`);
        setConfigs(prev => ({
          ...prev,
          [integrationId]: { ...getConfig(integrationId), status: 'connected' }
        }));
      } else {
        toast.error(res.data?.message || 'Connection test failed', { duration: 6000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connection test failed', { duration: 6000 });
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="integrations-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] text-white">Integrations</h1>
        <p className="text-slate-400 mt-1">Connect DotCall with your favorite tools and platforms</p>
      </div>

      <div className="grid gap-6">
        {INTEGRATIONS.map(integration => {
          const config = getConfig(integration.id);
          const isConnected = config.status === 'connected' && config.enabled;

          return (
            <div key={integration.id}
              className="glass-card rounded-xl p-6 border border-white/5 hover:border-white/10 transition-colors"
              data-testid={`integration-card-${integration.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0"
                    style={{ backgroundColor: integration.color + '20', color: integration.color }}>
                    {integration.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-white font-['Outfit']">{integration.name}</h3>
                      {isConnected && (
                        <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30" data-testid={`integration-status-${integration.id}`}>
                          <Check className="w-3 h-3 mr-1" /> Connected
                        </Badge>
                      )}
                      {config.enabled && !isConnected && (
                        <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/30">
                          Pending
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mt-0.5 max-w-xl">{integration.description}</p>
                    {config.last_sync && (
                      <p className="text-xs text-slate-500 mt-1" data-testid={`integration-last-sync-${integration.id}`}>
                        Last sync: {new Date(config.last_sync).toLocaleString()} —{' '}
                        <span className={config.last_sync_status === 'success' ? 'text-emerald-400' : 'text-red-400'}>
                          {config.last_sync_status}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Switch
                    checked={config.enabled || false}
                    onCheckedChange={() => toggleEnabled(integration.id)}
                    data-testid={`integration-toggle-${integration.id}`}
                  />
                </div>
              </div>

              {config.enabled && (
                <div className="mt-4 pt-4 border-t border-white/5 space-y-4 animate-fade-in">
                  <div className="grid gap-4 md:grid-cols-2">
                    {integration.fields.map(field => {
                      const value = config.fields?.[field.key] || '';
                      const fullWidth = field.type === 'textarea';
                      return (
                        <div key={field.key} className={fullWidth ? 'md:col-span-2' : ''}>
                          <Label className="text-slate-300 text-sm mb-2 block">{field.label}</Label>
                          {field.type === 'select' ? (
                            <select
                              value={value}
                              onChange={(e) => updateField(integration.id, field.key, e.target.value)}
                              data-testid={`integration-field-${integration.id}-${field.key}`}
                              className="w-full h-10 px-3 rounded-md bg-slate-950 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                            >
                              <option value="">— select —</option>
                              {field.options.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : field.type === 'textarea' ? (
                            <Textarea
                              value={value}
                              onChange={(e) => updateField(integration.id, field.key, e.target.value)}
                              placeholder={field.placeholder}
                              rows={3}
                              data-testid={`integration-field-${integration.id}-${field.key}`}
                              className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600"
                            />
                          ) : (
                            <Input
                              type={field.type}
                              value={value}
                              onChange={(e) => updateField(integration.id, field.key, e.target.value)}
                              placeholder={field.placeholder}
                              data-testid={`integration-field-${integration.id}-${field.key}`}
                              className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      onClick={() => handleSave(integration.id)}
                      disabled={saving === integration.id}
                      className="bg-amber-600 hover:bg-amber-500 text-white"
                      data-testid={`integration-save-${integration.id}`}>
                      {saving === integration.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
                      Save Configuration
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleTest(integration.id)}
                      disabled={testing === integration.id}
                      className="border-white/10 text-slate-300 hover:text-white hover:bg-white/5"
                      data-testid={`integration-test-${integration.id}`}>
                      {testing === integration.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Test Connection
                    </Button>
                    <a href={integration.docs} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-slate-500 hover:text-amber-400 flex items-center gap-1 ml-auto transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" /> Docs
                    </a>
                  </div>

                  {integration.extras === 'whatsapp' && (
                    <WhatsappTestPanel
                      config={config}
                      onAfterSend={(overrides) => {
                        if (overrides) {
                          // Toggling auto-send: persist + reload
                          handleSave(integration.id, overrides).then(reload);
                        } else {
                          reload();
                        }
                      }}
                    />
                  )}
                </div>
              )}

              {/* How-to-connect guide is always visible, regardless of toggle */}
              <HowToConnectPanel integrationId={integration.id} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
